// src/services/api.js
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// timeout กันค้างบน socket ที่ตายแล้ว (ต่อ request override ได้ เช่น backup ใช้ 300s)
const DEFAULT_TIMEOUT = 45000;

const api = axios.create({
  baseURL: API_BASE,
  timeout: DEFAULT_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
});

// แนบ token ทุก request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('wadphol_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const delay = (ms) => new Promise(r => setTimeout(r, ms));

// เมธอดที่ยิงซ้ำได้ปลอดภัย (ไม่ retry POST/PUT/DELETE — กันบันทึกซ้ำ)
const IDEMPOTENT_METHODS = ['get', 'head', 'options'];
// สถานะที่มักเกิดจาก shared hosting ตื่นจาก idle / worker เต็ม
const RETRYABLE_STATUSES = [408, 425, 429, 500, 502, 503, 504];
const MAX_RETRIES = 2;

/**
 * ยิงซ้ำได้หรือไม่
 * - network error (ไม่มี response) = keep-alive socket ตายหลัง idle, DNS, timeout
 * - 5xx/408/429 = server ยังตื่นไม่ทัน หรือ worker เต็ม
 */
function shouldRetry(err) {
  const config = err.config;
  if (!config || config.__noRetry) return false;
  if (err.code === 'ERR_CANCELED') return false;

  const method = (config.method || 'get').toLowerCase();
  if (!IDEMPOTENT_METHODS.includes(method)) return false;

  // timeout — ลองซ้ำครั้งเดียว ไม่ให้ผู้ใช้รอนานเกินไป
  const isTimeout = err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT';
  if (isTimeout) return (config.__retryCount || 0) < 1;

  if (!err.response) return true;
  return RETRYABLE_STATUSES.includes(err.response.status);
}

function clearAuthAndRedirect() {
  const path = window.location.pathname || '';
  const onLogin = /\/login\/?$/.test(path);
  const hadToken = localStorage.getItem('wadphol_token');
  localStorage.removeItem('wadphol_token');
  localStorage.removeItem('wadphol_user');
  if (hadToken && !onLogin) {
    const returnTo = `${path}${window.location.search || ''}`;
    sessionStorage.setItem('wadphol_return_to', returnTo);
    window.location.href = '/login';
  }
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err?.code === 'ERR_CANCELED') return Promise.reject(err);

    const config = err.config;

    // 1) ยิงซ้ำเมื่อ network error / server ยังไม่พร้อม (เฉพาะ GET)
    if (shouldRetry(err)) {
      const attempt = config.__retryCount || 0;
      if (attempt < MAX_RETRIES) {
        config.__retryCount = attempt + 1;
        await delay(400 * Math.pow(2, attempt) + Math.random() * 200);
        return api.request(config);
      }
    }

    // 2) 401 — ลองใหม่ 1 ครั้งก่อน logout (กัน false positive ตอน server ตื่น)
    if (err.response?.status === 401 && config && !config.__authRetried) {
      if (localStorage.getItem('wadphol_token')) {
        config.__authRetried = true;
        await delay(400);
        const token = localStorage.getItem('wadphol_token');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
          try {
            return await api.request(config);
          } catch (retryErr) {
            if (retryErr?.code === 'ERR_CANCELED') return Promise.reject(retryErr);
            err = retryErr;
          }
        }
      }
    }

    if (err.response?.status === 401) {
      clearAuthAndRedirect();
    }
    return Promise.reject(err);
  }
);

export default api;

// Auth
export const login = (data) => api.post('/auth/login.php', data);
export const getMe = () => api.get('/auth/me.php');

// Users
export const getUsers = (params) => api.get('/auth/users.php', { params });
export const createUser = (data) => api.post('/auth/users.php', data);
export const updateUser = (id, data) => api.put(`/auth/users.php?id=${id}`, data);
export const deleteUser = (id) => api.delete(`/auth/users.php?id=${id}`);
export const changePassword = (data) => api.post('/auth/change-password.php', data);

// Meta
export const getLevels = () => api.get('/subjects/groups.php?type=levels');
export const getGroups = () => api.get('/subjects/groups.php?type=groups');
export const getYears = () => api.get('/subjects/groups.php?type=years');
export const getCurrentYear = () => api.get('/subjects/groups.php?type=current_year');
export const createYear = (data) => api.post('/subjects/groups.php?type=years', data);
export const setCurrentYear = (id) => api.put(`/subjects/groups.php?type=years&id=${id}`, { is_current: 1 });
export const deleteYear = (id) => api.delete(`/subjects/groups.php?type=years&id=${id}`);

// Subjects
export const getSubjects = (params, config) => api.get('/subjects/index.php', { params, ...config });
export const createSubject      = (data)  => api.post('/subjects/index.php', data);
export const batchCreateSubjects = (items) => api.post('/subjects/batch.php', { items });
export const updateSubject = (id, data) => api.put(`/subjects/index.php?id=${id}`, data);
export const reorderSubjects = (items) => api.post('/subjects/index.php?action=reorder', { items });
export const copySubjectsFromYear = (sourceYearId, targetYearId, levelIds) => api.post('/subjects/index.php?action=copy_year', { source_year_id: sourceYearId, target_year_id: targetYearId, level_ids: levelIds || [] });
export const deleteSubject = (id) => api.delete(`/subjects/index.php?id=${id}`);
export const assignTeachersToSubjects = (data) => api.post('/subjects/assign_teachers.php', data);

// Classrooms
export const getClassrooms = (params, config) => api.get('/classes/index.php', { params, ...config });

// v2: เพิ่ม homeroom_teacher_id/2 ในผลลัพธ์ — ต้องขึ้น version เพื่อทิ้ง cache เดิมที่ยังไม่มีคอลัมน์นี้
const CLASSROOMS_CACHE_PREFIX = 'wadphol_classrooms_v2_';

export function readClassroomsCache(yearId) {
  try {
    const raw = sessionStorage.getItem(`${CLASSROOMS_CACHE_PREFIX}${yearId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeClassroomsCache(yearId, list) {
  try {
    sessionStorage.setItem(`${CLASSROOMS_CACHE_PREFIX}${yearId}`, JSON.stringify(list));
  } catch { /* ignore quota */ }
}

/** โหลดห้องเรียนแบบ lightweight (retry จัดการโดย interceptor กลาง) */
export async function getClassroomsReliable(yearId, config = {}) {
  const { retries, ...axiosConfig } = config; // eslint-disable-line no-unused-vars
  return getClassrooms({ academic_year_id: yearId, lightweight: 1 }, axiosConfig);
}

const SUBJECTS_CACHE_PREFIX = 'wadphol_subjects_';

export function readSubjectsCache(yearId, classroomId) {
  try {
    const raw = sessionStorage.getItem(`${SUBJECTS_CACHE_PREFIX}${yearId}_${classroomId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeSubjectsCache(yearId, classroomId, list) {
  try {
    sessionStorage.setItem(`${SUBJECTS_CACHE_PREFIX}${yearId}_${classroomId}`, JSON.stringify(list));
  } catch { /* ignore */ }
}

/** โหลดรายวิชาของห้อง (retry จัดการโดย interceptor กลาง) */
export async function getSubjectsReliable(yearId, classroomId, config = {}) {
  const { retries, ...axiosConfig } = config; // eslint-disable-line no-unused-vars
  return getSubjects(
    { academic_year_id: yearId, classroom_id: classroomId, lightweight: 1 },
    axiosConfig,
  );
}
export const getStudentSummary = (yearId) => api.get('/classes/index.php', { params: { academic_year_id: yearId, type: 'summary' } });
export const getStudentSummaryDetail = (yearId) => api.get('/classes/index.php', { params: { academic_year_id: yearId, type: 'summary_detail' } });
export const createClassroom = (data) => api.post('/classes/index.php', data);
export const updateClassroom = (id, data) => api.put(`/classes/index.php?id=${id}`, data);
export const deleteClassroom = (id) => api.delete(`/classes/index.php?id=${id}`);

// School Settings (ตั้งค่าโรงเรียน)
export const getSchoolSettings = () => api.get('/school/settings.php');
export const getYearSettings    = (yearId) => api.get('/school/year_settings.php', { params: { academic_year_id: yearId } });
export const saveYearSettings   = (data)   => api.put('/school/year_settings.php', data);
export const copyYearSettings   = (data)   => api.post('/school/year_settings.php', data);
export const updateSchoolSettings = (data) => api.put('/school/settings.php', data);
export const resetData = (type, academic_year_id) => api.delete('/school/reset.php', { data: { type, academic_year_id } });
export const uploadSchoolLogo = (formData) => api.post('/school/upload_logo.php', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteSchoolLogo = () => api.delete('/school/upload_logo.php');

export const uploadObecLogo = (formData) => api.post('/school/upload_obec_logo.php', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteObecLogo = () => api.delete('/school/upload_obec_logo.php');

// Quality Items (คุณลักษณะ / อ่านคิด / สมรรถนะ / IS)
export const getQualityItems    = (params) => api.get('/school/quality_items.php', { params });
export const createQualityItem  = (data)   => api.post('/school/quality_items.php', data);
export const updateQualityItem  = (id, data) => api.put(`/school/quality_items.php?id=${id}`, data);
export const deleteQualityItem  = (id)     => api.delete(`/school/quality_items.php?id=${id}`);
export const reorderQualityItems = (items) => api.patch('/school/quality_items.php', { items });

// Quality Types (หมวด — เปิด/ปิด/เพิ่มหมวด)
export const getQualityTypes    = (params) => api.get('/school/quality_types.php', { params });
export const createQualityType  = (data)   => api.post('/school/quality_types.php', data);
export const updateQualityType  = (id, data) => api.put(`/school/quality_types.php?id=${id}`, data);
export const deleteQualityType  = (id)     => api.delete(`/school/quality_types.php?id=${id}`);

// Quality Scores (คะแนน คุณลักษณะ / อ่านคิด / สมรรถนะ / IS)
export const getQualityInit         = (params) => api.get('/scores/quality_init.php',        { params }); // super-loader
export const getScoreClassroomsInit = ()       => api.get('/scores/classrooms_init.php');          // super-loader ScoreClassroomPage
export const getSubjectResult        = (params) => api.get('/scores/subject_result.php', { params }); // ผลสอบรายวิชา
export const getQualityScores  = (params) => api.get('/scores/quality_scores.php', { params });
export const saveQualityScores = (data)   => api.post('/scores/quality_scores.php', data);
export const getQualityProgress = (params) => api.get('/scores/quality_progress.php', { params });
export const getQualityReport  = (params) => api.get('/scores/quality_report.php', { params });
export const getClassroomReport = (params) => api.get('/scores/classroom_report.php', { params });
export const getEvaluationReport = (params) => api.get('/scores/evaluation_report.php', { params });

// Classroom Settings (ตั้งค่าจำนวนห้อง)
export const getClassroomSettings = (params, config) => api.get('/classes/settings.php', { params, ...config });
export const saveClassroomSettings = (data) => api.post('/classes/settings.php', data);
export const updateClassroomSettings = (id, data) => api.put(`/classes/settings.php?id=${id}`, data);
export const syncClassroomSettings = (year_id) => api.patch(`/classes/settings.php?academic_year_id=${year_id}`);

// Students
export const getStudents = (params) => api.get('/students/index.php', { params });
export const createStudent = (data) => api.post('/students/index.php', data);
export const batchCreateStudents = (data) => api.post('/students/batch.php', data);
export const updateStudent = (id, data) => api.put(`/students/index.php?id=${id}`, data);
export const reorderSeats = (data) => api.put('/students/index.php', data);
export const deleteStudent = (id, params) => api.delete(`/students/index.php?id=${id}`, { params });
export const transferStudent = (data) => api.patch('/students/index.php', data);
export const getTransferHistory = (studentId, yearId) => api.get('/students/index.php', { params: { type: 'transfer_history', student_id: studentId, ...(yearId ? { academic_year_id: yearId } : {}) } });

// Learning Units
export const getUnits = (params) => api.get('/units/index.php', { params });
export const createUnit = (data) => api.post('/units/index.php', data);
export const updateUnit = (id, data) => api.put(`/units/index.php?id=${id}`, data);
export const deleteUnit = (id) => api.delete(`/units/index.php?id=${id}`);

// Strands


// Indicators
export const getIndicators = (params) => api.get('/indicators/index.php', { params });
export const createIndicator       = (data)  => api.post('/indicators/index.php', data);
export const batchCreateIndicators = (items) => api.post('/indicators/batch.php', { items });
export const batchImportIndicators = (data)  => api.post('/indicators/batch_import.php', data);
export const updateIndicator = (id, data) => api.put(`/indicators/index.php?id=${id}`, data);
export const deleteIndicator = (id) => api.delete(`/indicators/index.php?id=${id}`);
export const copyIndicators = (data) => api.post('/indicators/index.php?action=copy', data);

// Scores
export const getScoreGrid = (params) => api.get('/scores/index.php', { params });
export const saveScores = (data) => api.post('/scores/index.php', data);
export const saveUnitScores = (data) => api.post('/scores/index.php', data); // sub_unit mode: { unit_scores: [...] }
export const getActivityResults = (params) => api.get('/scores/activity_results.php', { params });
export const getClubSummary = (params) => api.get('/scores/club_summary.php', { params });
export const getFinalScores = (params) => api.get('/scores/final.php', { params });
export const saveFinalScores = (data) => api.post('/scores/final.php', data);

// Teachers (ครูและบุคลากรทางการศึกษา)
export const getTeachers = (params, config) => api.get('/teachers/index.php', { params, ...config });
export const getTeacher = (id) => api.get(`/teachers/index.php?id=${id}`);
export const createTeacher = (data) => api.post('/teachers/index.php', data);
export const updateTeacher = (id, data) => api.put(`/teachers/index.php?id=${id}`, data);
export const reorderTeachers = (order) => api.post('/teachers/index.php', { action: 'reorder', order });
export const deleteTeacher = (id) => api.delete(`/teachers/index.php?id=${id}`);
export const copyTeachersFromYear = (sourceYearId, targetYearId) => api.post('/teachers/index.php', { action: 'copy_year', source_year_id: sourceYearId, target_year_id: targetYearId });
export const uploadTeacherPhoto = (formData) => api.post('/teachers/upload_photo.php', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Clubs (ชุมนุม)
export const getClubs          = (params) => api.get('/clubs/index.php', { params });
export const getClub           = (id) => api.get(`/clubs/index.php?id=${id}`);
export const createClub        = (data) => api.post('/clubs/index.php', data);
export const updateClub        = (id, data) => api.put(`/clubs/index.php?id=${id}`, data);
export const deleteClub        = (id) => api.delete(`/clubs/index.php?id=${id}`);

// Club Objectives (จุดประสงค์ชุมนุม)
export const getClubObjectives     = (params) => api.get('/clubs/objectives.php', { params });
export const saveClubObjectives    = (data) => api.post('/clubs/objectives.php', data);
export const updateClubObjective   = (id, data) => api.put(`/clubs/objectives.php?id=${id}`, data);
export const deleteClubObjective   = (id) => api.delete(`/clubs/objectives.php?id=${id}`);

// Club Members (สมาชิกชุมนุม)
export const getClubMembers    = (params) => api.get('/clubs/members.php', { params });
export const checkStudentClubs = (params) => api.get('/clubs/members.php', { params: { ...params, type: 'check_clubs' } });
export const addClubMembers    = (data) => api.post('/clubs/members.php', data);
export const removeClubMember  = (id) => api.delete(`/clubs/members.php?id=${id}`);

// Club Registration Report (รายงานภาพรวมการลงทะเบียนชุมนุม)
export const getClubRegistrationReport = (params) => api.get('/clubs/registration_report.php', { params });

// Club Scores (คะแนนชุมนุม)
export const getClubScores     = (params) => api.get('/clubs/scores.php', { params });
export const saveClubScores    = (data) => api.post('/clubs/scores.php', data);

// Club Attendance (เช็คชื่อชุมนุม)
export const getClubAttendance  = (params) => api.get('/clubs/attendance.php', { params });
export const saveClubAttendance = (data) => api.post('/clubs/attendance.php', data);
export const getClubSchedule    = (params) => api.get('/clubs/attendance.php', { params: { type: 'club_schedule', ...params } });

// Dashboard Stats (admin/academic)
export const getDashboardStats = () => api.get('/school/dashboard_stats.php');

// Attendance Daily Summary
export const getAttendanceDailySummary = (params) => api.get('/attendance/daily_summary.php', { params });

// ─── ระบบเช็คชื่อรายคาบ (Attendance) ───
// คาบเรียน
export const getPeriods       = (params, config) => api.get('/attendance/periods.php', { params, ...config });
export const createPeriod     = (data) => api.post('/attendance/periods.php', data);
export const updatePeriod     = (id, data) => api.put(`/attendance/periods.php?id=${id}`, data);
export const deletePeriod     = (id) => api.delete(`/attendance/periods.php?id=${id}`);

// ตารางสอน
export const getSchedules     = (params) => api.get('/attendance/schedules.php', { params });
export const checkScheduleSlot = (params) => api.get('/attendance/schedules.php', { params: { ...params, check_slot: 1 } });
export const saveSchedules    = (data) => api.post('/attendance/schedules.php', data);
export const deleteSchedules  = (params) => api.delete('/attendance/schedules.php', { params });
export const importSchedules  = (data) => api.post('/attendance/schedule_import.php', data);

// วันหยุด / วันสอนชดเชย
export const getHolidays      = (params, config) => api.get('/attendance/holidays.php', { params, ...config });
export const createHoliday    = (data) => api.post('/attendance/holidays.php', data);
export const updateHoliday    = (id, data) => api.put(`/attendance/holidays.php?id=${id}`, data);
export const deleteHoliday    = (id) => api.delete(`/attendance/holidays.php?id=${id}`);

// เช็คชื่อ
export const getAttendance    = (params) => api.get('/attendance/index.php', { params });
export const saveAttendance   = (data) => api.post('/attendance/index.php', data);
export const getAttendanceReport   = (params) => api.get('/attendance/report.php', { params });
export const getAttendanceSummary  = (params) => api.get('/attendance/attendance_summary.php', { params });
export const getClubAttendanceReport = (params) => api.get('/clubs/attendance_report.php', { params });

// สอนแทน
export const getSubstitutes         = (params) => api.get('/attendance/substitute.php', { params });
export const createSubstitute       = (data)   => api.post('/attendance/substitute.php', data);
export const updateSubstitute       = (id, data) => api.put('/attendance/substitute.php', { id, ...data });
export const deleteSubstitute       = (id)     => api.delete(`/attendance/substitute.php?id=${id}`);export const getAvailableTeachers   = (params) => api.get('/attendance/available_teachers.php', { params });
export const getSubstituteRecordPdfUrl  = (params) => { const q = new URLSearchParams(params).toString(); return `${API_BASE}/attendance/substitute_record_pdf.php?${q}`; };
export const getSubstituteSummaryPdfUrl = (params) => { const q = new URLSearchParams(params).toString(); return `${API_BASE}/attendance/substitute_summary_pdf.php?${q}`; };

// Feature permissions (per-user)
export const getFeaturePermissions   = (feature_key) => api.get('/auth/feature_permissions.php', { params: { feature_key } });
export const setFeaturePermissions   = (feature_key, user_ids) => api.put('/auth/feature_permissions.php', { feature_key, user_ids });
export const checkMyFeaturePermission = (feature_key) => api.get('/auth/feature_permissions.php', { params: { feature_key, mine: 1 } });

// กิจกรรมซ่อมเสริม
export const getRemedialActivities    = (params) => api.get('/remedial/activities.php', { params });
export const createRemedialActivity   = (data)   => api.post('/remedial/activities.php', data);
export const updateRemedialActivity   = (id, data) => api.put(`/remedial/activities.php?id=${id}`, data);
export const deleteRemedialActivity   = (id)     => api.delete(`/remedial/activities.php?id=${id}`);
export const getRemedialSchedules     = (params) => api.get('/remedial/activities.php', { params: { type: 'schedules', ...params } });
export const saveRemedialSchedules    = (data)   => api.post('/remedial/activities.php', { action: 'save_schedules', ...data });
export const getRemedialAttendance    = (params) => api.get('/remedial/attendance.php', { params });
export const saveRemedialAttendance   = (data)   => api.post('/remedial/attendance.php', data);

// ─── ระบบสำรองข้อมูล (Backup) ───
export const getBackups          = (params) => api.get('/backup/index.php', { params });
export const runBackupNow        = () => api.post('/backup/index.php', {}, { timeout: 300000 });
export const deleteBackup        = (id) => api.delete(`/backup/index.php?id=${id}`);
export const bulkDeleteBackups   = (ids) => api.delete('/backup/index.php', { data: { ids } });
export const getBackupSettings   = () => api.get('/backup/settings.php');
export const saveBackupSettings  = (data) => api.put('/backup/settings.php', data);
export const testGoogleDrive     = () => api.post('/backup/settings.php?action=test', {}, { timeout: 60000 });
// URL ดาวน์โหลดไฟล์ backup (เปิดหน้าต่างใหม่พร้อมแนบ token)
export const backupDownloadUrl   = (id) => {
  const token = localStorage.getItem('wadphol_token');
  return `${API_BASE}/backup/download.php?id=${id}&token=${encodeURIComponent(token || '')}`;
};
// URL เริ่มกระบวนการเชื่อมต่อ Google Drive แบบ OAuth (เปิด popup)
export const backupOauthStartUrl = () => {
  const token = localStorage.getItem('wadphol_token');
  return `${API_BASE}/backup/oauth.php?action=start&token=${encodeURIComponent(token || '')}`;
};

